import type {
  PortfolioAccount,
  PortfolioPeriod,
} from '@/modules/projects/components/portfolio/types'
import { cashBeyondHoldings } from '@/modules/projects/components/portfolio/usePortfolioDashboard'
import { NUMBER_MASK } from '@/modules/projects/components/Workbench/accounts'
import type {
  Statement,
  StatementSegment,
} from '@/modules/projects/stores/types'
import { toCalendarDate } from '@/utils'
import { shortInstitutionName } from './institutionName'
import {
  monthOf,
  monthsApart,
  monthsBetween,
  monthsEarlier,
  nextMonth,
  printedMonthsOf,
} from './months'
import {
  accountNotReadReason,
  failedReadReason,
  isFailedRead,
  statementReasons,
  twoInstitutionsReason,
} from './statementReasons'
import type {
  AccountStatement,
  AccountTimeline,
  MonthKey,
  VerifyingStatement,
} from './types'

const HOLDINGS_SECTION = 'holdings_market_value'

export const accountName = (institution: string, last4: string): string =>
  last4
    ? `${shortInstitutionName(institution)} ${NUMBER_MASK} ${last4}`
    : shortInstitutionName(institution)

const printedHoldingsTotal = (period: PortfolioPeriod): number | null =>
  period.printedTotals.find((total) => total.section === HOLDINGS_SECTION)
    ?.amount ?? null

const securitiesOf = (
  period: PortfolioPeriod,
  holdingsSum: number,
  printed: number | null,
): number | null => {
  if (printed != null) return printed
  if (period.holdings.length > 0) return holdingsSum
  return period.cashBalance == null ? null : 0
}

const toAccountStatement = (
  period: PortfolioPeriod,
  endDate: string,
): AccountStatement => {
  const month = monthOf(endDate)
  const holdingsSum = period.holdings.reduce(
    (total, holding) => total + (holding.marketValue ?? 0),
    0,
  )
  const printed = printedHoldingsTotal(period)
  const securities = securitiesOf(period, holdingsSum, printed)
  const cash =
    period.cashBalance == null
      ? null
      : cashBeyondHoldings(period.cashBalance, period.holdings)
  return {
    month,
    startDate: period.startDate,
    endDate,
    coveredMonths: printedMonthsOf(period.startDate, endDate),
    statementId: period.statementId,
    segmentId: period.segmentId,
    statementFileName: period.statementFileName,
    statementFileUrl: period.statementFileUrl,
    holdingsSum,
    printedHoldingsTotal: printed,
    securities,
    countedAtPrintedTotal:
      printed != null && Math.round((printed - holdingsSum) * 100) !== 0,
    cash,
    value: securities == null ? null : securities + (cash ?? 0),
    reasons: statementReasons(period, month),
  }
}

// A statement whose securities could not be read, placed like one being verified. leftOut: the read
// worked for other accounts on the statement but returned none of last4s.
export type FailedRead = VerifyingStatement & { leftOut: boolean }

const unreadReason = (read: FailedRead, last4: string) =>
  read.leftOut
    ? accountNotReadReason(read, read.month, last4)
    : failedReadReason(read, read.month)

const failedReadStatement = (
  read: FailedRead,
  last4: string,
): AccountStatement => ({
  month: read.month,
  startDate: read.startDate,
  endDate: read.endDate,
  coveredMonths: read.coveredMonths,
  statementId: read.statementId,
  segmentId: read.segmentId,
  statementFileName: read.statementFileName,
  statementFileUrl: read.statementFileUrl,
  holdingsSum: 0,
  printedHoldingsTotal: null,
  securities: null,
  countedAtPrintedTotal: false,
  cash: null,
  value: null,
  reasons: [unreadReason(read, last4)],
})

// The most common value, the smallest one on a tie so a doubtful cadence hides no gap.
const mostCommon = (samples: number[]): number | null => {
  const counts = new Map<number, number>()
  for (const sample of samples) {
    counts.set(sample, (counts.get(sample) ?? 0) + 1)
  }
  const [best] = [...counts].sort(
    ([firstSample, firstCount], [secondSample, secondCount]) =>
      secondCount - firstCount || firstSample - secondSample,
  )
  return best?.[0] ?? null
}

// How many months one statement of this account usually spans: the gaps between end months, plus
// the span of every statement that prints its start date. Unknown with fewer than two statements.
const cadenceOf = (statements: AccountStatement[]): number | null => {
  if (statements.length < 2) return null
  const endMonths = [...new Set(statements.map((statement) => statement.month))]
  const gaps = endMonths
    .slice(1)
    .map((month, position) => monthsApart(endMonths[position], month))
  const printedSpans = statements
    .filter((statement) => statement.startDate)
    .map((statement) => statement.coveredMonths.length)
  return mostCommon([...gaps, ...printedSpans])
}

const laterMonth = (first: MonthKey, second: MonthKey) =>
  first > second ? first : second

// A statement with no start date runs back toward the one before it, but no further than the
// account's cadence: a quarterly statement covers its quarter, a monthly one leaves a hole open.
const withUnprintedStartsFilled = (
  statements: AccountStatement[],
): AccountStatement[] => {
  const cadence = cadenceOf(statements)
  return statements.map((statement, position) => {
    const previous = statements[position - 1]
    const isFillable =
      !statement.startDate &&
      cadence != null &&
      previous != null &&
      previous.month < statement.month
    if (!isFillable) return statement
    const firstMonth = laterMonth(
      nextMonth(previous.month),
      monthsEarlier(statement.month, cadence - 1),
    )
    return {
      ...statement,
      coveredMonths: monthsBetween(firstMonth, statement.month),
    }
  })
}

const readMonths = (account: PortfolioAccount) =>
  new Set(
    account.periods.flatMap((period) =>
      period.endDate ? [monthOf(period.endDate)] : [],
    ),
  )

// A read that left the account out adds nothing when the account has its own read for that month.
const unreadStatementsOf = (
  account: PortfolioAccount,
  failedReads: FailedRead[],
): AccountStatement[] => {
  const months = readMonths(account)
  return failedReads
    .filter((read) => !read.leftOut || !months.has(read.month))
    .map((read) => failedReadStatement(read, account.accountNumber))
}

const timelineStatements = (
  account: PortfolioAccount,
  failedReads: FailedRead[],
): AccountStatement[] =>
  withUnprintedStartsFilled(
    [
      ...account.periods.flatMap((period) =>
        period.endDate ? [toAccountStatement(period, period.endDate)] : [],
      ),
      ...unreadStatementsOf(account, failedReads),
    ].sort((first, second) => first.endDate.localeCompare(second.endDate)),
  )

const printedMonthsOfStatement = (statement: AccountStatement) =>
  printedMonthsOf(statement.startDate, statement.endDate)

const readMonthsOf = (timelines: AccountTimeline[]) =>
  new Set(
    timelines.flatMap((timeline) =>
      timeline.statements
        .filter((statement) => !isFailedRead(statement))
        .flatMap(printedMonthsOfStatement),
    ),
  )

// Two accounts share a last 4 only when two institutions report it for one month. Each statement
// that covers such a month says so.
const withSharedLast4Reasons = (
  timelines: AccountTimeline[],
): AccountTimeline[] =>
  timelines.map((timeline) => {
    const others = timelines.filter(
      (other) =>
        other !== timeline && timeline.last4 && other.last4 === timeline.last4,
    )
    if (others.length === 0) return timeline
    const otherMonths = readMonthsOf(others)
    return {
      ...timeline,
      statements: timeline.statements.map((statement) => {
        const sharedMonth = isFailedRead(statement)
          ? undefined
          : printedMonthsOfStatement(statement).find((month) =>
              otherMonths.has(month),
            )
        if (!sharedMonth) return statement
        const reason = twoInstitutionsReason(
          statement,
          statement.month,
          sharedMonth,
          timeline.last4,
        )
        return { ...statement, reasons: [...statement.reasons, reason] }
      }),
    }
  })

const namesAccount =
  (account: PortfolioAccount) => (statement: { last4s: string[] }) =>
    statement.last4s.includes(account.accountNumber)

export const buildAccountTimelines = (
  accounts: PortfolioAccount[],
  verifying: VerifyingStatement[],
  failedReads: FailedRead[] = [],
): AccountTimeline[] =>
  withSharedLast4Reasons(
    accounts.map((account) => ({
      id: account.id,
      last4: account.accountNumber,
      institution: account.institution,
      name: accountName(account.institution, account.accountNumber),
      accountType: account.accountType,
      statements: timelineStatements(
        account,
        failedReads.filter(namesAccount(account)),
      ),
      verifying: verifying.filter(namesAccount(account)),
    })),
  )

const isVerifying = (segment: StatementSegment) =>
  segment.securitiesDetail?.status === 'processing' &&
  segment.duplicateOfSegmentId == null

// A held read carries no accounts, so the cycle's own rows and balances name them.
const segmentLast4s = (segment: StatementSegment): string[] => {
  const last4s = new Set<string>()
  for (const account of segment.accountNumbers ?? []) last4s.add(account.last4)
  for (const account of segment.reconciliationDetail?.accounts ?? []) {
    last4s.add(account.last4)
  }
  return Array.from(last4s).filter(Boolean)
}

const placeSegment = (
  statement: Statement,
  segment: StatementSegment,
  segmentEndDate: string,
): VerifyingStatement => {
  const endDate = toCalendarDate(segmentEndDate)
  const startDate = segment.startDate ? toCalendarDate(segment.startDate) : null
  return {
    segmentId: segment.id,
    statementId: statement.id,
    statementFileName: statement.fileName,
    statementFileUrl: statement.fileUrl,
    startDate,
    endDate,
    month: monthOf(endDate),
    coveredMonths: printedMonthsOf(startDate, endDate),
    last4s: segmentLast4s(segment),
  }
}

const placeSegmentsWhere = (
  statements: Statement[],
  isWanted: (segment: StatementSegment) => boolean,
): VerifyingStatement[] =>
  statements.flatMap((statement) =>
    (statement.segments ?? []).flatMap((segment) =>
      isWanted(segment) && segment.endDate
        ? [placeSegment(statement, segment, segment.endDate)]
        : [],
    ),
  )

// Brokerage statements still being read or checked by the team, placed by their period.
export const findVerifyingStatements = (
  statements: Statement[],
): VerifyingStatement[] => placeSegmentsWhere(statements, isVerifying)

// lukita stores "failed" when the read dies. A needs_review read with no accounts found a
// securities side but could not read any account from it.
const isFailedReadSegment = (segment: StatementSegment) => {
  const status = segment.securitiesDetail?.status
  const hasNoAccounts = (segment.securitiesDetail?.accounts?.length ?? 0) === 0
  return (
    segment.duplicateOfSegmentId == null &&
    (status === 'failed' || (status === 'needs_review' && hasNoAccounts))
  )
}

const readLast4s = (segment: StatementSegment) =>
  new Set(
    (segment.securitiesDetail?.accounts ?? []).map(
      (account) => account.accountLast4,
    ),
  )

// The cycle names the account (its own rows or a balance chain), yet the read returned only others.
const leftOutReadsOf = (
  statement: Statement,
  segment: StatementSegment,
): FailedRead[] => {
  const read = readLast4s(segment)
  const isPartialRead =
    read.size > 0 &&
    segment.endDate != null &&
    segment.duplicateOfSegmentId == null &&
    !isVerifying(segment) &&
    !isFailedReadSegment(segment)
  if (!isPartialRead || !segment.endDate) return []
  const leftOut = segmentLast4s(segment).filter((last4) => !read.has(last4))
  if (leftOut.length === 0) return []
  const placed = placeSegment(statement, segment, segment.endDate)
  return [{ ...placed, last4s: leftOut, leftOut: true }]
}

// Brokerage statements the customer uploaded whose securities could not be read for an account,
// placed by their period: the whole read failed, or it left that account out.
export const findFailedReads = (statements: Statement[]): FailedRead[] => [
  ...placeSegmentsWhere(statements, isFailedReadSegment).map((read) => ({
    ...read,
    leftOut: false,
  })),
  ...statements.flatMap((statement) =>
    (statement.segments ?? []).flatMap((segment) =>
      leftOutReadsOf(statement, segment),
    ),
  ),
]

// Statements of one account that end in the same month, two or more per group, oldest month first.
export const statementsSharingAMonth = (
  timeline: AccountTimeline,
): AccountStatement[][] => {
  const byMonth = new Map<string, AccountStatement[]>()
  for (const statement of timeline.statements) {
    byMonth.set(statement.month, [
      ...(byMonth.get(statement.month) ?? []),
      statement,
    ])
  }
  return Array.from(byMonth.values()).filter((group) => group.length > 1)
}
